import React from 'react';
import {
  AnswerInput,
  ProgramQuestionsResult,
  ProgramQuestionWithAnswer,
  ProgramQuestionsSection,
  QuestionStatus,
  SectionStatus,
} from './types';

export const UNSECTIONED_SECTION_KEY = '__unsectioned__';

export const LONG_TEXT_MIN_LENGTH = 150;
export const LONG_TEXT_MAX_LENGTH = 900;

export interface ProgramTagContext {
  programName: string;
  beneficiaryTerm?: string | null;
  serviceGoal?: string | null;
}

export const DEFAULT_SERVICE_GOAL = 'match your service goal';

const programNameReplacement: {
  text: (ctx: ProgramTagContext) => string;
  node: (ctx: ProgramTagContext) => React.ReactNode;
} = {
  text: ({ programName }) => programName,
  node: ({ programName }) => React.createElement('i', null, programName),
};

const beneficiaryReplacement: {
  text: (ctx: ProgramTagContext) => string;
  node: (ctx: ProgramTagContext) => React.ReactNode;
} = {
  text: ({ beneficiaryTerm }) => beneficiaryTerm || 'your beneficiaries',
  node: ({ beneficiaryTerm }) => beneficiaryTerm || 'your beneficiaries',
};

export const PROGRAM_TAG_REPLACERS: Record<
  string,
  {
    text: (ctx: ProgramTagContext) => string;
    node: (ctx: ProgramTagContext) => React.ReactNode;
  }
> = {
  '<Program_name>': programNameReplacement,
  '<Program Name>': programNameReplacement,
  '<Primary_beneficiary_or_Intermediary>': beneficiaryReplacement,
  '<Primary_beneficiary>': beneficiaryReplacement,
  '<Service_goal>': {
    text: ({ serviceGoal }) => serviceGoal || DEFAULT_SERVICE_GOAL,
    node: ({ serviceGoal }) => serviceGoal || DEFAULT_SERVICE_GOAL,
  },
};

/**
 * Resolve the `<Service_goal>` replacement the same way ReportBuilder
 * does: find the Q40002 collection, then the Q40003 service in it, and
 * read its `goal`. Returns null when either answer or the data is
 * missing so callers can fall back to DEFAULT_SERVICE_GOAL.
 */
export const resolveServiceGoal = (
  collections:
    | ReadonlyArray<{
        name: string;
        services: ReadonlyArray<{ name: string; goal?: string | null }>;
      }>
    | null
    | undefined,
  collectionName: string | null | undefined,
  serviceName: string | null | undefined,
): string | null => {
  if (!collections || !collectionName || !serviceName) return null;
  const trim = (s: string) => s.replace(/\u200b/g, '').trim();
  const collection = collections.find(
    c => trim(c.name) === trim(collectionName),
  );
  const service = collection?.services.find(
    s => trim(s.name) === trim(serviceName),
  );
  return service?.goal || null;
};

const toTagContext = (
  ctx: string | ProgramTagContext,
): ProgramTagContext =>
  typeof ctx === 'string' ? { programName: ctx } : ctx;

const PROGRAM_TAG_SPLIT_REGEX = new RegExp(
  `(${Object.keys(PROGRAM_TAG_REPLACERS)
    .map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')})`,
  'g',
);

export const replaceProgramTags = <T extends string | null | undefined>(
  text: T,
  context: string | ProgramTagContext,
): T => {
  if (text == null) return text;
  const ctx = toTagContext(context);
  let out = text as string;
  for (const [tag, { text: resolve }] of Object.entries(
    PROGRAM_TAG_REPLACERS,
  )) {
    if (out.includes(tag)) {
      out = out.split(tag).join(resolve(ctx));
    }
  }
  return out as T;
};

export const buildProgramTextNode = (
  text: string | null | undefined,
  context: string | ProgramTagContext,
): React.ReactNode => {
  if (!text) return null;
  const ctx = toTagContext(context);
  const parts = text.split(PROGRAM_TAG_SPLIT_REGEX);
  return parts.map((part, i) => {
    const replacer = PROGRAM_TAG_REPLACERS[part];
    if (replacer) {
      return React.createElement(
        React.Fragment,
        { key: i },
        replacer.node(ctx),
      );
    }
    return part;
  });
};

export const applyProgramTagsToQuestions = (
  result: ProgramQuestionsResult | null,
  programName: string,
  extra: Omit<ProgramTagContext, 'programName'> = {},
): ProgramQuestionsResult | null => {
  if (!result) return result;
  // Resolve the beneficiary term from the raw result so question text
  // can embed it (e.g. Q40022 "...who in turn serve <Primary_beneficiary>").
  // Callers may pre-supply it (e.g. from a program-profile fallback).
  const beneficiaryTerm =
    extra.beneficiaryTerm ??
    findQuestion(result, BENEFICIARY_TERM_QUESTION_ID)?.question.answer
      ?.text ??
    null;
  const ctx: ProgramTagContext = { programName, ...extra, beneficiaryTerm };
  return {
    ...result,
    sections: result.sections.map(section => ({
      ...section,
      questions: section.questions.map(q => ({
        ...q,
        text: replaceProgramTags(q.text, ctx),
        textNode: buildProgramTextNode(q.text, ctx),
      })),
    })),
  };
};

export const sectionKey = (section: string | null | undefined): string =>
  section && section.trim().length > 0 ? section : UNSECTIONED_SECTION_KEY;

export const BENEFICIARY_TERM_QUESTION_ID = 40004;
export const SERVICE_TYPE_QUESTION_ID = 40006;
export const SOCIAL_IMPACT_QUESTION_ID = 40010;
export const REACH_INDICATORS_QUESTION_ID = 40011;
export const CAPACITY_DEVELOPMENT_QUESTION_ID = 40021;
export const INTERMEDIARIES_QUESTION_ID = 40022;

/**
 * Fixed order for the impact_summary section from the beneficiary-term
 * question onward (matches the ReportBuilder sequence, where reach
 * indicators 40011 come before social-impact indicators 40010).
 * Questions before 40004 keep server order; questions not listed here
 * follow in server order after 40010.
 */
const IMPACT_SUMMARY_FLOW: number[] = [
  BENEFICIARY_TERM_QUESTION_ID,
  CAPACITY_DEVELOPMENT_QUESTION_ID,
  SERVICE_TYPE_QUESTION_ID,
  INTERMEDIARIES_QUESTION_ID,
  REACH_INDICATORS_QUESTION_ID,
  SOCIAL_IMPACT_QUESTION_ID,
];

/** Second (indirect) option of Q40006; the text embeds the beneficiary term. */
export const INDIRECT_SERVICE_TYPE_REGEX = /To family members, caregivers, volunteers, or other intermediaries, who in turn serve .+/;

/**
 * Q40022 (intermediaries) is only part of the flow when Q40006 was
 * answered "indirectly". Prefer the branch the backend encodes on the
 * chosen enum option (`nextQuestionId`), falling back to matching the
 * stored option text since it is not static.
 */
export const isIntermediariesQuestionVisible = (
  serviceType: ProgramQuestionWithAnswer | null | undefined,
): boolean => {
  const answer = serviceType?.answer;
  if (!serviceType || !answer) return false;
  const next =
    answer.enumResponse?.nextQuestionId ??
    serviceType.enumResponses.find(o => o.dbId === answer.answerId)
      ?.nextQuestionId ??
    null;
  if (next === INTERMEDIARIES_QUESTION_ID) return true;
  if (next != null) return false;
  return INDIRECT_SERVICE_TYPE_REGEX.test(
    answer.text ?? answer.questionText ?? '',
  );
};

const recountSection = (
  section: ProgramQuestionsSection,
  questions: ProgramQuestionWithAnswer[],
): ProgramQuestionsSection => ({
  ...section,
  questions,
  total: questions.length,
  answered: questions.filter(questionIsAnswered).length,
  firstUnansweredQuestionId:
    questions.find(q => !questionIsAnswered(q))?.questionId ?? null,
});

const recountResult = (
  result: ProgramQuestionsResult,
  sections: ProgramQuestionsSection[],
): ProgramQuestionsResult => {
  const totalQuestions = sections.reduce((n, s) => n + s.total, 0);
  const answeredCount = sections.reduce((n, s) => n + s.answered, 0);
  return {
    ...result,
    sections,
    totalQuestions,
    answeredCount,
    completed: totalQuestions > 0 && answeredCount === totalQuestions,
    nextUnansweredQuestionId:
      sections.map(s => s.firstUnansweredQuestionId).find(id => id != null) ??
      null,
  };
};

/**
 * Re-sequence the impact_summary section to the fixed flow above and drop
 * Q40022 unless Q40006 says "indirectly". Sidebar, flat navigation, review
 * screen and completion counters all read from the returned list, so the
 * hidden question is excluded everywhere consistently. No-op for results
 * without an impact_summary section containing Q40004.
 */
export const applyImpactSummaryFlow = (
  result: ProgramQuestionsResult | null,
): ProgramQuestionsResult | null => {
  if (!result) return result;
  let changed = false;
  const sections = result.sections.map(section => {
    if (sectionKey(section.section) !== 'impact_summary') return section;
    const anchorIdx = section.questions.findIndex(
      q => q.questionId === BENEFICIARY_TERM_QUESTION_ID,
    );
    if (anchorIdx === -1) return section;
    const flowIds = new Set(IMPACT_SUMMARY_FLOW);
    const byId = new Map(section.questions.map(q => [q.questionId, q]));
    const before = section.questions
      .slice(0, anchorIdx)
      .filter(q => !flowIds.has(q.questionId));
    const flow = IMPACT_SUMMARY_FLOW.map(id => byId.get(id)).filter(
      (q): q is ProgramQuestionWithAnswer => q != null,
    );
    const rest = section.questions
      .slice(anchorIdx)
      .filter(q => !flowIds.has(q.questionId));
    const showIntermediaries = isIntermediariesQuestionVisible(
      byId.get(SERVICE_TYPE_QUESTION_ID),
    );
    const ordered = [...before, ...flow, ...rest].filter(
      q => showIntermediaries || q.questionId !== INTERMEDIARIES_QUESTION_ID,
    );
    changed = true;
    return recountSection(section, ordered);
  });
  return changed ? recountResult(result, sections) : result;
};

/**
 * A question is answered iff any of text/date1/date2/answerId is set.
 * Empty strings do NOT count — mirrors the backend's is_answered rule.
 */
export const isAnswerFilled = (
  input: AnswerInput | null | undefined,
): boolean => {
  if (!input) return false;
  const { text, date1, date2, answerId } = input;
  if (text != null && text.trim().length > 0) return true;
  if (date1 != null && String(date1).length > 0) return true;
  if (date2 != null && String(date2).length > 0) return true;
  if (answerId != null) return true;
  return false;
};

export const questionIsAnswered = (q: ProgramQuestionWithAnswer): boolean => {
  if (q.isAnswered) return true;
  return isAnswerFilled(q.answer);
};

export const sectionIsComplete = (s: ProgramQuestionsSection): boolean =>
  s.total > 0 && s.answered >= s.total;

export const getSectionStatus = (
  section: ProgramQuestionsSection,
  attemptedSubmit: boolean,
): SectionStatus => {
  if (sectionIsComplete(section)) return 'complete';
  return attemptedSubmit ? 'missing' : 'incomplete';
};

export const getQuestionStatus = (
  question: ProgramQuestionWithAnswer,
  attemptedSubmit: boolean,
): QuestionStatus => {
  if (questionIsAnswered(question)) return 'complete';
  return attemptedSubmit ? 'missing' : 'incomplete';
};

/**
 * Find a question anywhere in the sections list by id.
 */
export const findQuestion = (
  result: ProgramQuestionsResult | null,
  questionId: number | null | undefined,
): {
  section: ProgramQuestionsSection;
  question: ProgramQuestionWithAnswer;
} | null => {
  if (!result || questionId == null) return null;
  for (const s of result.sections) {
    const q = s.questions.find(q => q.questionId === questionId);
    if (q) return { section: s, question: q };
  }
  return null;
};

/**
 * Pick the initial question to land on when the wizard opens.
 * Preference: nextUnansweredQuestionId → first section's first question.
 */
export const pickInitialQuestionId = (
  result: ProgramQuestionsResult | null,
): number | null => {
  if (!result) return null;
  if (result.nextUnansweredQuestionId != null) {
    return result.nextUnansweredQuestionId;
  }
  const firstSection = result.sections[0];
  return firstSection?.questions[0]?.questionId ?? null;
};


/**
 * Maps a source questionId to the direct child questionIds whose answers
 * must be cleared when the source's `text` changes. Only direct children
 * are cleared — cascades are not applied recursively. Populate as new
 * dependencies are identified.
 */
export const QUESTION_CLEAR_MAP: Record<number, number[]> = {
  40002: [40003, 40010, 40011],
  40003: [40010, 40011],
  110001: [110002, 110003, 110004, 110005, 110006],
  110002: [110003, 110004, 110005, 110006],
};

/**
 * Patch a ProgramQuestionsResult in place-immutably so that every question
 * whose id is in `clearedIds` is treated as unanswered (`text: ''`,
 * `isAnswered: false`). Section `answered` counters and the top-level
 * `answeredCount` are recomputed. Used to reflect a bulk-clear locally
 * without a refetch.
 */
export const clearAnswersInQuestions = (
  prev: ProgramQuestionsResult,
  clearedIds: number[],
): ProgramQuestionsResult => {
  if (!clearedIds.length) return prev;
  const clearedSet = new Set(clearedIds);
  const nextSections = prev.sections.map(section => {
    let answeredDelta = 0;
    const nextQuestions = section.questions.map(q => {
      if (!clearedSet.has(q.questionId)) return q;
      if (questionIsAnswered(q)) answeredDelta -= 1;
      const clearedAnswer = q.answer
        ? { ...q.answer, text: '', date1: null, date2: null, answerId: null }
        : null;
      return { ...q, answer: clearedAnswer, isAnswered: false };
    });
    if (answeredDelta === 0 && nextQuestions === section.questions) {
      return section;
    }
    return {
      ...section,
      questions: nextQuestions,
      answered: Math.max(0, section.answered + answeredDelta),
    };
  });
  const answeredCount = nextSections.reduce((n, s) => n + s.answered, 0);
  return { ...prev, sections: nextSections, answeredCount };
};

/**
 * The stored answer for Q40011 is a JSON array of the selected indicator
 * *names* — same shape the generic checkbox flow uses. Per-selection
 * `successCriteria` and `forecast` are persisted separately via
 * `updateSuccessCriteria` / `saveReportIndicators` and are NOT round-tripped
 * through this text.
 */
export const parseReachIndicatorNames = (
  raw: string | null | undefined,
): string[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((n): n is string => typeof n === 'string');
  } catch {
    return [];
  }
};

/**
 * Sentinel value written to Q40011's answer text by `ReachIndicatorsField`
 * when the user has selected indicators but hasn't clicked "Save details"
 * for all of them. It intentionally does not parse as a JSON array of names,
 * so `parseReachIndicatorNames` returns [] and validation fails — but with
 * a targeted message rather than the generic "Select at least one".
 */
export const REACH_INDICATORS_UNSAVED_MARKER = '__REACH_INDICATORS_UNSAVED__';

/**
 * Basic client-side validation before persisting an answer.
 * Returns an error message or null when valid.
 */
export const validateAnswerInput = (
  question: ProgramQuestionWithAnswer,
  input: AnswerInput,
): string | null => {
  if (question.questionId === REACH_INDICATORS_QUESTION_ID) {
    if (input.text === REACH_INDICATORS_UNSAVED_MARKER) {
      return 'Click "Save details" for each selected reach indicator before continuing.';
    }
    if (parseReachIndicatorNames(input.text).length === 0) {
      return 'Select at least one reach indicator.';
    }
    return null;
  }
  if (question.required && !isAnswerFilled(input)) {
    return 'This question is required.';
  }
  const rawType = (question.questionType ?? '').toLowerCase();
  const isLongText =
    !rawType ||
    rawType.includes('textarea') ||
    rawType.includes('long') ||
    rawType.includes('paragraph');
  if (isLongText && input.text) {
    const len = input.text.length;
    if (len < LONG_TEXT_MIN_LENGTH || len > LONG_TEXT_MAX_LENGTH) {
      return `Answer must be between ${LONG_TEXT_MIN_LENGTH} and ${LONG_TEXT_MAX_LENGTH} characters.`;
    }
  }
  for (const v of question.validations) {
    if (v.validationType === 'required' && !isAnswerFilled(input)) {
      return v.text || 'This question is required.';
    }
    if (v.validationType === 'regex' && input.text && v.validation) {
      try {
        const re = new RegExp(v.validation);
        if (!re.test(input.text)) return v.text || 'Invalid format.';
      } catch {
        // Ignore malformed regex — backend will re-validate.
      }
    }
  }
  return null;
};

/**
 * Given a section, return its first unanswered question id, or null if
 * the section is complete.
 */
export const firstUnansweredIn = (
  section: ProgramQuestionsSection | null | undefined,
): number | null => {
  if (!section) return null;
  if (section.firstUnansweredQuestionId != null) {
    return section.firstUnansweredQuestionId;
  }
  const q = section.questions.find(q => !questionIsAnswered(q));
  return q?.questionId ?? null;
};
